import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { TID } from "@/lib/testIds";

const links = [
  { id: "why", label: "Solutions", testid: TID.nav.linkServices },
  { id: "services", label: "Why Choose", testid: "nav-link-why-choose" },
  { id: "about", label: "About", testid: TID.nav.linkAbout },
  { id: "process", label: "Process", testid: TID.nav.linkProcess },
  { id: "careers", label: "Careers", testid: "nav-link-careers" },
  { id: "contact", label: "Contact", testid: TID.nav.linkContact },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid={TID.nav.container}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <button
          data-testid={TID.nav.logo}
          onClick={() => scrollTo("hero")}
          className="flex items-center group"
        >
          <img
            src="/jmr-logo.png?v=2"
            alt="JMR Global Logistics — Your Freight, Our Priority"
            className={`h-16 md:h-20 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollTo(l.id)}
              className={`text-base font-semibold tracking-wide transition-colors hover:text-sky-400 ${
                scrolled ? "text-slate-700" : "text-white/90"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid={TID.nav.ctaQuote}
            onClick={() => scrollTo("quote")}
            className="bg-sky-400 text-blue-950 font-semibold text-base px-6 py-3 hover:bg-sky-300 transition-colors"
          >
            Request a Quote →
          </button>
        </nav>

        <button
          data-testid={TID.nav.mobileToggle}
          className={`lg:hidden p-2 ${scrolled ? "text-slate-900" : "text-white"}`}
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-slate-800 font-semibold py-2 hover:text-sky-600"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("quote")}
              className="bg-sky-400 text-slate-900 font-semibold px-5 py-3 mt-2"
            >
              Request a Quote →
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
